import { useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { CheckCircle2, XCircle, Terminal } from "lucide-react";

const ROWS = [
  { name: "checkout › applies discount code", status: "pass", time: "1.21s" },
  { name: "auth › resets password via email", status: "pass", time: "0.84s" },
  { name: "payments › declines expired card", status: "fail", time: "2.37s" },
  { name: "search › filters by date range", status: "pass", time: "1.02s" },
  { name: "roles › blocks unauthorised export", status: "pass", time: "0.67s" },
];

export default function QAHeroVisual() {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useTransform(my, [0, 1], [7, -7]);
  const rotateY = useTransform(mx, [0, 1], [-9, 9]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0.5); my.set(0.5); }}
      className="relative [perspective:1200px]"
      data-testid="hero-qa-visual"
    >
      <div
        className="absolute -inset-10 rounded-full bg-volt/[0.05] blur-[100px]"
        aria-hidden="true"
      />
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative"
      >
        <div className="relative rounded-2xl border border-zinc-800 bg-ink-900/90 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)] backdrop-blur-sm">
          <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3.5">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
              <span className="h-2.5 w-2.5 rounded-full bg-volt/70" />
            </div>
            <div className="flex items-center gap-2 font-mono text-[11px] text-zinc-500">
              <Terminal className="h-3.5 w-3.5" />
              avirova / test-runner — release 2.4.0
            </div>
          </div>

          <div className="space-y-1 px-3 py-4">
            {ROWS.map((row, i) => (
              <motion.div
                key={row.name}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 + i * 0.14, duration: 0.5 }}
                className="flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors duration-300 hover:bg-white/[0.03]"
              >
                <div className="flex items-center gap-3">
                  {row.status === "pass" ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-volt" />
                  ) : (
                    <XCircle className="h-4 w-4 shrink-0 text-red-400" />
                  )}
                  <span className="font-mono text-xs text-zinc-300">{row.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[10px] text-zinc-600">{row.time}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider ${
                      row.status === "pass"
                        ? "bg-volt/10 text-volt"
                        : "bg-red-400/10 text-red-400"
                    }`}
                  >
                    {row.status === "pass" ? "Passed" : "Failed"}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="border-t border-zinc-800 px-5 py-4">
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-zinc-500">
              <span>Regression suite</span>
              <span>128 / 142 tests</span>
            </div>
            <div className="mt-2 h-1 overflow-hidden rounded-full bg-zinc-800">
              <motion.div
                className="h-full rounded-full bg-volt"
                initial={{ width: "0%" }}
                animate={{ width: "90%" }}
                transition={{ delay: 1.6, duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
            <div className="mt-3 flex gap-2">
              <span className="rounded-md border border-zinc-800 px-2 py-1 font-mono text-[10px] text-zinc-400">121 passed</span>
              <span className="rounded-md border border-red-400/30 px-2 py-1 font-mono text-[10px] text-red-400">1 critical</span>
              <span className="rounded-md border border-zinc-800 px-2 py-1 font-mono text-[10px] text-zinc-400">6 blocked</span>
            </div>
          </div>
        </div>

        <motion.div
          style={{ transform: "translateZ(50px)" }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="absolute -bottom-6 -left-4 rounded-xl border border-zinc-800 bg-ink-800/95 px-4 py-3 shadow-2xl backdrop-blur-sm sm:-left-10"
        >
          <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">API check</p>
          <p className="mt-1 font-mono text-xs text-zinc-200">
            POST /v1/orders <span className="text-volt">→ 200 OK</span>
            <span className="text-zinc-500"> · 84ms</span>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
